import React, { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import TableRestaurantRoundedIcon from "@mui/icons-material/TableRestaurantRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import { Icon } from "@iconify/react";
import { guestCopy } from "data/guestCopy";
import { withGuestSearch } from "data/guestPortalDefaults";
import { LUXURY, guestDisplayName } from "data/guestLuxuryTheme";
import ImageLoader from "components/ImageLoader";
import { useGuestHubSearch } from "../GuestHubSearchContext";
import WifiSheet from "./WifiSheet";

const hour = () => new Date().getHours();
const GUTTER = 18;

const GuestHeader = ({ session }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { searchOpen, toggleSearch } = useGuestHubSearch();
  const [wifiOpen, setWifiOpen] = useState(false);
  const isHub = location.pathname === "/guest" || location.pathname === "/guest/";
  const stay = session.portalConfig?.stay || {};
  const greeting =
    hour() < 12
      ? guestCopy.welcomeMorning
      : hour() < 17
        ? guestCopy.welcomeAfternoon
        : guestCopy.welcomeEvening;
  const guestName = guestDisplayName(session.customerProfile);
  const hotelName = session.company?.name || "The Grandview Hotel & Resort";
  const unread = (session.requests || []).filter((item) => item.status !== "cancelled").length;
  const locationHint =
    session.table?.location ||
    (session.isRoom ? "Guest room" : "Dining");

  const openWifiSheet = () => setWifiOpen(true);

  return (
    <Box
      sx={{
        position: isHub ? "relative" : "sticky",
        top: 0,
        zIndex: 1100,
        color: "#fff",
        mb: isHub ? 5.2 : 0,
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "visible",
          backgroundColor: LUXURY.forestDeep,
          borderBottomLeftRadius: isHub ? 0 : 22,
          borderBottomRightRadius: isHub ? 0 : 22,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            borderBottomLeftRadius: isHub ? 0 : 22,
            borderBottomRightRadius: isHub ? 0 : 22,
            backgroundImage: `url(${LUXURY.headerImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center 38%",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background: isHub
                ? LUXURY.headerGradient
                : "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.78) 100%)",
            },
          }}
        />

        <Box
          sx={{
            position: "relative",
            maxWidth: LUXURY.maxWidth,
            mx: "auto",
            px: `${GUTTER}px`,
            pt: "max(14px, env(safe-area-inset-top))",
            pb: isHub ? 7.2 : 1.35,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.25,
              minHeight: 36,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  color: LUXURY.gold,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {session.company?.logo ? (
                  <ImageLoader
                    imagePath={session.company.logo}
                    width={28}
                    height={28}
                    alt={hotelName}
                    rounded={false}
                  />
                ) : (
                  <Icon icon="mdi:spa-outline" width={26} height={26} color="currentColor" />
                )}
              </Box>
              <Typography
                sx={{
                  minWidth: 0,
                  fontFamily: LUXURY.displayFont,
                  fontWeight: 500,
                  fontSize: "0.72rem",
                  letterSpacing: "0.04em",
                  color: LUXURY.gold,
                  lineHeight: 1.15,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {hotelName}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
              {isHub ? (
                <IconButton
                  onClick={toggleSearch}
                  aria-label={searchOpen ? "Close search" : "Search"}
                  aria-pressed={searchOpen}
                  sx={{
                    p: 0,
                    width: 38,
                    height: 38,
                    bgcolor: searchOpen ? "rgba(201,166,107,0.22)" : "rgba(0,0,0,0.35)",
                    backdropFilter: "blur(10px)",
                    border: `1px solid ${searchOpen ? LUXURY.gold : LUXURY.glassBorder}`,
                    color: LUXURY.gold,
                    "&:hover": {
                      bgcolor: searchOpen ? "rgba(201,166,107,0.28)" : "rgba(0,0,0,0.45)",
                    },
                  }}
                >
                  <SearchRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : null}
              <IconButton
                onClick={() => navigate(withGuestSearch("/guest/requests", session.guestSearch))}
                aria-label="Notifications"
                sx={{
                  p: 0,
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(0,0,0,0.35)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${LUXURY.glassBorder}`,
                  color: LUXURY.gold,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.45)" },
                }}
              >
                <NotificationsNoneRoundedIcon sx={{ fontSize: 18 }} />
                {unread > 0 ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 9,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: "#E85D4C",
                    }}
                  />
                ) : null}
              </IconButton>
            </Box>
          </Box>

          {isHub ? (
            <Box
              sx={{
                mt: 1.4,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: LUXURY.displayFont,
                    fontWeight: 500,
                    fontSize: guestName ? "1.35rem" : "2rem",
                    lineHeight: 1.1,
                    color: "#fff",
                    wordBreak: "break-word",
                    textShadow: "0 8px 24px rgba(0,0,0,0.35)",
                  }}
                >
                  {greeting}
                  {guestName ? "," : ""}
                </Typography>
                {guestName ? (
                  <Typography
                    sx={{
                      mt: 0.15,
                      fontFamily: LUXURY.displayFont,
                      fontWeight: 600,
                      fontSize: "1.85rem",
                      lineHeight: 1.08,
                      color: "#fff",
                      wordBreak: "break-word",
                    }}
                  >
                    {guestName}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      mt: 0.45,
                      fontSize: "0.78rem",
                      color: "rgba(255,255,255,0.72)",
                      lineHeight: 1.35,
                      maxWidth: 220,
                    }}
                  >
                    Welcome to your stay
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 0.2,
                  pt: 0.35,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
                  <WbSunnyOutlinedIcon sx={{ fontSize: 16, color: LUXURY.gold }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#fff" }}>
                    28°C
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.62)" }}>
                  Colombo
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>

      {isHub ? (
        <Box
          sx={{
            position: "absolute",
            left: GUTTER,
            right: GUTTER,
            bottom: 0,
            transform: "translateY(50%)",
            maxWidth: LUXURY.maxWidth,
            mx: "auto",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={openWifiSheet}
            aria-label="View Wi-Fi connection details"
            sx={{
              appearance: "none",
              WebkitTapHighlightColor: "transparent",
              border: `1px solid ${LUXURY.glassBorder}`,
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1.15,
              minHeight: 64,
              px: 1.45,
              py: 1.05,
              borderRadius: "20px",
              bgcolor: LUXURY.glass,
              backdropFilter: "blur(18px)",
              color: "#fff",
              cursor: "pointer",
              boxShadow: "0 14px 32px rgba(0,0,0,0.35)",
              textAlign: "left",
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.08)",
                color: LUXURY.gold,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {session.isRoom ? (
                <BedOutlinedIcon sx={{ fontSize: 20 }} />
              ) : (
                <TableRestaurantRoundedIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.92rem",
                  color: "#fff",
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {session.locationLabel}
              </Typography>
              <Typography
                sx={{
                  mt: 0.2,
                  fontSize: "0.7rem",
                  color: "rgba(255,255,255,0.55)",
                  lineHeight: 1.25,
                  wordBreak: "break-word",
                }}
              >
                {locationHint}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.35,
                flexShrink: 0,
                color: LUXURY.gold,
              }}
            >
              <WifiRoundedIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: LUXURY.gold }}>
                Connected
              </Typography>
              <ChevronRightRoundedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.45)" }} />
            </Box>
          </Box>
        </Box>
      ) : null}

      <WifiSheet
        open={wifiOpen}
        onClose={() => setWifiOpen(false)}
        wifiName={stay.wifiName}
        wifiPassword={stay.wifiPassword}
      />
    </Box>
  );
};

export default GuestHeader;
